import React, { createContext, useContext, useEffect, useState } from 'react'
import { AlertColor, AlertPropsColorOverrides } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

import { TrayType as TrayCategory } from 'components/organisms/AddTraySelectType/AddTraySelectType.types'
import { InventorySheetFile } from './InventorySheets/InventorySheets.types'
import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { AddTrayContextProps, TrayItem } from './AddTray.types'
import { OverridableStringUnion } from '@mui/types'
import { ItemResponse, SubItem, Surgery } from 'common/types'
import { TrayType } from 'components/organisms/EditTrayModal/EditTrayModal.types'
import useSubmitTrayCapture from 'views/DigitalTrayMapping/CaptureTray/CaptureCamera/SubmitTrayCapture.logic'
import { getAnalysisJSONFileName } from 'lib/utils/getAnalysisJSONFile'
import { useLocation } from 'react-router-dom'
import { SubTray } from './SubTrays/SubTrays.types'
import { useInventorySheetsContext } from './InventorySheets/InventorySheets.context'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { TrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'
import dayjs from 'lib/dayjs'
import { useUser } from 'app/User'
import { useEditSubTrayItemMutation, useUpdateTrayItem } from 'lib/apollo/hooks'
import { enqueueSnackbar } from 'notistack'
const AddTrayContext = createContext<AddTrayContextProps | undefined>(undefined)

export const AddTrayProvider: React.FC = ({ children }) => {
  const { pathname } = useLocation()

  const { user } = useUser()
  const [templateData, setTemplateData] = useState<any>(null)
  const {
    analysisFileName,
    setAnalysisFileName,
    handleSubmit: handleSubmitAnalysis,
    analysisScrews,
    setIsAnalysisFromSPD,
    isComplete,
    setIsComplete,
    analysisError,
    setAnalysisError,
    resetSubmitTrayCapture,
    updateTemplateData,
  } = useSubmitTrayCapture(templateData)
  const { inventorySheetFiles, setInventorySheetFiles } =
    useInventorySheetsContext()
  const { editTrayItem } = useUpdateTrayItem()
  const { editSubTrayItem } = useEditSubTrayItemMutation()

  const [traySurgeryId, setTraySurgeryId] = useState<Surgery['_id']>('')
  const [traySurgery, setTraySurgery] = useState<Surgery | undefined>(undefined)
  const [trayBarcode, setTrayBarcode] = useState<string>('')
  const [showScanner, setShowScanner] = useState<boolean>(false)
  const [trayDescription, setTrayDescription] = useState<string>('')
  const [selectedLocationId, setSelectedLocationId] = useState<
    number | undefined
  >(undefined)
  const [trayVendor, setTrayVendor] = useState<string>('')
  const [trayWeight, setTrayWeight] = useState<number | undefined>(undefined)
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [snackbarState, setSnackbarState] = useState<
    OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined
  >('success')
  const [snackbarMessage, setSnackbarMessage] = useState<string>('')
  const [snackbarIcon, setSnackbarIcon] = useState<JSX.Element>(
    <CheckCircle color="success" />
  )
  const [trayAnalysisScrews, setTrayAnalysisScrews] = useState<
    BetterIDTrayScrew[]
  >([])
  const [trayImage, setTrayImage] = useState<InventorySheetFile | undefined>(
    undefined
  )
  const [selectedTrayCategory, setSelectedTrayCategory] =
    useState<TrayCategory['category']>('facility')
  const [selectedTrayType, setSelectedTrayType] = useState<TrayType>(undefined)
  const [isAnalysisComplete, setIsAnalysisComplete] = useState<boolean>(false)
  const [subTrays, setSubTrays] = useState<SubTray[]>([])
  const [selectedSurgeon, setSelectedSurgeon] = useState<string>('')
  const [trayAddConfirmation, setTrayAddConfirmation] = useState<
    boolean | undefined
  >()
  const [hasSubTrays, setHasSubTrays] = useState<boolean>(true)
  const [parentTrayImage, setParentTrayImage] = useState<
    InventorySheetFile | undefined
  >(undefined)

  // New state for multiple trays
  const [trays, setTrays] = useState<TrayItem[]>([])
  const [currentTrayIndex, setCurrentTrayIndex] = useState<number>(-1)
  const [trayItem, setTrayItem] = useState<ItemResponse | undefined>(undefined)

  // New state for tracking if we're fixing a rejected tray
  const [isFixingRejectedTray, setIsFixingRejectedTray] =
    useState<boolean>(false)
  const [rejectedTrayId, setRejectedTrayId] = useState<string>('')

  const [selectedTraySetName, setSelectedTraySetName] = useState<string>('')

  useEffect(() => {
    if (selectedTrayType) {
      const analysisFileName = getAnalysisJSONFileName(selectedTrayType)

      setAnalysisFileName(analysisFileName)
    }
  }, [selectedTrayType, pathname, setAnalysisFileName])

  useEffect(() => {
    updateTemplateData(templateData)
  }, [templateData, updateTemplateData])

  /**
   * Handles closing the snackbar.
   * @param {React.SyntheticEvent | Event} event - The event object.
   * @param {string | undefined} reason - The reason for closing the snackbar.
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string | undefined
  ) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbarOpen(false)
  }

  /**
   * Resets the add tray context to its initial state
   * but preserves location and surgery information
   */
  const resetAddTrayContext = () => {
    // Save the values we want to preserve
    const preservedLocationId = selectedLocationId
    const preservedSurgeryId = traySurgeryId
    const preservedSurgery = traySurgery
    const preservedSurgeon = selectedSurgeon

    // Reset everything
    setTrayBarcode('')
    setShowScanner(false)
    setTrayDescription('')
    setTrayVendor('')
    setTrayWeight(undefined)
    setTrayImage(undefined)
    setTrayAnalysisScrews([])
    setSelectedTrayCategory('facility')
    setSelectedTrayType(undefined)
    setIsAnalysisComplete(false)
    setAnalysisFileName('')
    setIsAnalysisFromSPD(false)
    setIsComplete(false)
    setAnalysisError('')
    setSubTrays([])
    setHasSubTrays(false)
    setParentTrayImage(undefined)
    setInventorySheetFiles([])

    // Restore the preserved values
    setSelectedLocationId(preservedLocationId)
    setTraySurgeryId(preservedSurgeryId)
    setTraySurgery(preservedSurgery)
    setSelectedSurgeon(preservedSurgeon)
  }

  /**
   * Saves the current tray configuration as a new tray item
   */
  const saveCurrentTrayAsItem = (subTray?: SubTray) => {
    const newTrayItem: TrayItem = {
      id: uuidv4(),
      trayBarcode,
      trayDescription,
      selectedLocationId,
      trayVendor,
      trayWeight,
      trayAnalysisScrews,
      trayImage,
      selectedTrayCategory,
      selectedTrayType,
      isAnalysisComplete,
      subTrays: subTray ? [subTray] : subTrays,
      selectedSurgeon,
      hasSubTrays,
      parentTrayImage,
      inventorySheetFiles,
      surgery: traySurgery,
    }

    setTrays((prevTrays) => [...prevTrays, newTrayItem])
    if (!isFixingRejectedTray) {
      resetAddTrayContext()
    }
  }

  /**
   * Loads a tray item into the current context for editing
   */
  const loadTrayItem = (index: number) => {
    if (index >= 0 && index < trays.length) {
      const tray = trays[index]
      setTrayBarcode(tray.trayBarcode)
      setTrayDescription(tray.trayDescription)
      setSelectedLocationId(tray.selectedLocationId)
      setTrayVendor(tray.trayVendor)
      setTrayWeight(tray.trayWeight)
      setTrayAnalysisScrews(tray.trayAnalysisScrews)
      setTrayImage(tray.trayImage)
      setSelectedTrayCategory(tray.selectedTrayCategory)
      setSelectedTrayType(tray.selectedTrayType)
      setIsAnalysisComplete(tray.isAnalysisComplete)
      setSubTrays(tray.subTrays)
      setSelectedSurgeon(tray.selectedSurgeon)
      setHasSubTrays(tray.hasSubTrays)
      setParentTrayImage(tray.parentTrayImage)
      setCurrentTrayIndex(index)
    }
  }

  /**
   * Updates the current tray in the trays array
   */
  const updateCurrentTrayItem = (subTrayArray?: SubTray[]) => {
    if (currentTrayIndex >= 0) {
      const updatedTrayItem: TrayItem = {
        id: trays[currentTrayIndex].id,
        trayBarcode,
        trayDescription,
        selectedLocationId,
        trayVendor,
        trayWeight,
        trayAnalysisScrews,
        trayImage,
        selectedTrayCategory,
        selectedTrayType,
        isAnalysisComplete,
        subTrays: subTrayArray || subTrays,
        selectedSurgeon,
        hasSubTrays,
        parentTrayImage,
        inventorySheetFiles,
        surgery: traySurgery,
      }

      setTrays((prevTrays) => {
        const newTrays = [...prevTrays]
        newTrays[currentTrayIndex] = updatedTrayItem
        return newTrays
      })
    }
  }

  /**
   * Removes a tray from the trays array
   */
  const removeTrayItem = (index: number) => {
    setTrays((prevTrays) => {
      const newTrays = [...prevTrays]
      newTrays.splice(index, 1)
      return newTrays
    })

    if (currentTrayIndex === index) {
      resetAddTrayContext()
      setCurrentTrayIndex(-1)
    } else if (currentTrayIndex > index) {
      setCurrentTrayIndex(currentTrayIndex - 1)
    }
  }

  const resetAllTrays = () => {
    resetAddTrayContext()
    setTrays([])
    setCurrentTrayIndex(-1)
  }

  /**
   * Loads data from a rejected tray into the current context for fixing
   */
  const loadRejectedTrayData = (trayItem: ItemResponse) => {
    try {
      setTrayItem(trayItem)
      const trayDetails = extractTrayJSONData(trayItem)

      if (!trayDetails) {
        console.error('Failed to extract tray details')
        return
      }

      setRejectedTrayId(trayItem.barcode || trayItem.id.toString())
      setIsFixingRejectedTray(true)

      setTrayBarcode(trayItem.barcode || '')
      setTrayDescription(trayItem.description || '')
      setSelectedLocationId(trayItem.locationId)
      setTrayVendor(trayItem.companyName || '')

      setSelectedTrayCategory(trayDetails.trayCategory || 'facility')
      setSelectedTrayType(trayDetails.trayType)

      if (trayDetails.selectedSurgeon) {
        setSelectedSurgeon(trayDetails.selectedSurgeon)
      }

      if (trayDetails.surgeryId && trayDetails.surgeryDetails) {
        setTraySurgeryId(trayDetails.surgeryId)
        setTraySurgery(trayDetails.surgeryDetails)
      }

      if (trayItem.subItems && trayItem.subItems.length > 0) {
        const processedSubTrays = trayItem.subItems.map(
          (subItem: SubItem, index: number) => {
            const subTrayDetails = extractSubTrayJSONData(subItem)

            return {
              trayNumber: subItem.id,
              trayImg: subTrayDetails?.trayImg || '',
              trayType: subTrayDetails?.trayType || 'stryker screw caddy',
              parLevel: subTrayDetails?.parLevel || 1,
              content: {
                screws: subTrayDetails?.screws || [],
                plates: subTrayDetails?.plates || [],
                otherItems: subTrayDetails?.otherItems || [],
                customImplants: subTrayDetails?.customImplants || [],
              },
              isInstrumentTray: subTrayDetails?.trayType === 'instrument',
              description: subItem.description || '',
            }
          }
        )
        setHasSubTrays(true)
        setSubTrays(processedSubTrays)
      } else {
        setHasSubTrays(false)
      }

      if (trayDetails.trayImage) {
        setTrayImage({
          id: trayDetails.trayImage,
          src: trayDetails.trayImage,
          isUploading: false,
          isUploaded: true,
          mimeType: 'image/jpeg',
        })
      }

      if (trayDetails.trayImage) {
        setParentTrayImage({
          id: trayDetails.trayImage,
          src: trayDetails.trayImage,
          isUploading: false,
          isUploaded: true,
          mimeType: 'image/jpeg',
        })
      }

      if (
        trayDetails.inventorySheetFiles &&
        trayDetails.inventorySheetFiles.length > 0
      ) {
        setInventorySheetFiles(
          trayDetails.inventorySheetFiles.map((sheet: InventorySheetFile) => ({
            id: sheet.id,
            src: sheet.src,
            isUploading: sheet.isUploading,
            isUploaded: sheet.isUploaded,
            mimeType: sheet.mimeType,
          }))
        )
      }
    } catch (error) {
      console.error('Error loading rejected tray data:', error)
    }
  }

  /**
   * Updates the rejected tray with fixed data
   */
  const updateRejectedTray = async () => {
    if (!isFixingRejectedTray || !rejectedTrayId || !trayItem) {
      return false
    }

    if (!trayBarcode) {
      enqueueSnackbar('Please enter a tray barcode', {
        variant: 'error',
      })
      return false
    }

    try {
      const oldParentDetails = extractTrayJSONData(trayItem)

      if (!oldParentDetails) {
        console.error('Failed to extract old parent details')
        return false
      }

      const updatedParentDetails: TrayItemProductDetails = {
        ...oldParentDetails,
        inventorySheetFiles: inventorySheetFiles.map((file) => ({
          id: file.id,
          src: file.src,
          mimeType: file.mimeType,
        })),
        trayImage: parentTrayImage?.src || oldParentDetails.trayImage,
        status: 'droppedOff',
        statusDetails: {
          lastUpdatedAt: dayjs().toISOString(),
          lastUpdatedBy: user?.email,
        },
      }

      const { subItems, ...trayItemWithoutSubItems } = trayItem

      const updatedParentTray: ItemResponse = {
        ...trayItemWithoutSubItems,
        barcode: trayBarcode,
        description: trayDescription,
        locationId: selectedLocationId,
        companyName: trayVendor,
        productDetails: JSON.stringify(updatedParentDetails),
      }

      const editResult = await editTrayItem(updatedParentTray)

      if (!editResult || !editResult.editTrayItem) {
        enqueueSnackbar('Failed to edit tray item.', {
          variant: 'error',
        })
        throw new Error('Failed to edit tray item.')
      }

      if (subTrays.length > 0) {
        const subTrayUpdates = await Promise.all(
          subTrays.map(async (subTray, index) => {
            const trayImageRecord = {
              imageUrl: subTray.trayImg,
              addedBy: {
                email: user?.email || '',
                role: 'rep',
              },
              timestamp: new Date(),
            }

            const trayImageHistory = {
              records: [trayImageRecord],
              latest: {
                rep: trayImageRecord,
              },
            }

            const newProductDetails = JSON.stringify({
              parLevel: subTray.parLevel,
              trayImg: subTray.trayImg,
              trayImageHistory: trayImageHistory,
              trayType: subTray.trayType,
              screws: subTray.content.screws,
              plates: subTray.content.plates,
              description: subTray.description,
              customImplants: subTray.content.customImplants,
              otherItems: subTray.content.otherItems,
            })

            try {
              const editSubResult = await editSubTrayItem({
                id: Number(subTray.trayNumber),
                productDetails: newProductDetails,
              })

              if (!editSubResult) {
                console.error(`Failed to update sub-tray ${subTray.trayNumber}`)
              }

              return editSubResult
            } catch (error) {
              console.error(
                `Error updating sub-tray ${subTray.trayNumber}:`,
                error
              )
              return null
            }
          })
        )

        // Check if any sub-tray updates failed
        const failedUpdates = subTrayUpdates.filter((result) => result === null)
        if (failedUpdates.length > 0) {
          enqueueSnackbar(`${failedUpdates.length} sub-tray updates failed.`, {
            variant: 'warning',
          })
        }
      }

      return true
    } catch (error) {
      console.error('Error updating rejected tray:', error)
      return false
    }
    setSelectedSurgeon('')
    setTrayAddConfirmation(undefined)
    setTemplateData(null)
  }

  return (
    <AddTrayContext.Provider
      value={{
        traySurgeryId,
        setTraySurgeryId,
        traySurgery,
        setTraySurgery,
        trayBarcode,
        setTrayBarcode,
        showScanner,
        setShowScanner,
        trayDescription,
        setTrayDescription,
        selectedLocationId,
        setSelectedLocationId,
        trayVendor,
        setTrayVendor,
        trayWeight,
        setTrayWeight,
        snackbarOpen,
        setSnackbarOpen,
        snackbarState,
        setSnackbarState,
        snackbarMessage,
        setSnackbarMessage,
        snackbarIcon,
        setSnackbarIcon,
        handleSnackbarClose,
        trayAnalysisScrews,
        setTrayAnalysisScrews,
        trayImage,
        setTrayImage,
        selectedTrayCategory,
        setSelectedTrayCategory,
        selectedTrayType,
        setSelectedTrayType,
        isAnalysisComplete,
        setIsAnalysisComplete,
        analysisFileName,
        setAnalysisFileName,
        handleSubmitAnalysis,
        analysisScrews,
        setIsAnalysisFromSPD,
        isComplete,
        setIsComplete,
        analysisError,
        setAnalysisError,
        subTrays,
        setSubTrays,
        selectedSurgeon,
        setSelectedSurgeon,
        setTrayAddConfirmation,
        trayAddConfirmation,
        resetAddTrayContext,
        hasSubTrays,
        setHasSubTrays,
        parentTrayImage,
        setParentTrayImage,
        trays,
        setTrays,
        currentTrayIndex,
        setCurrentTrayIndex,
        saveCurrentTrayAsItem,
        loadTrayItem,
        updateCurrentTrayItem,
        removeTrayItem,
        resetAllTrays,
        isFixingRejectedTray,
        setIsFixingRejectedTray,
        rejectedTrayId,
        setRejectedTrayId,
        loadRejectedTrayData,
        updateRejectedTray,
        templateData,
        setTemplateData,
        selectedTraySetName,
        setSelectedTraySetName,
      }}
    >
      {children}
    </AddTrayContext.Provider>
  )
}

/**
 * Custom hook to access the AddTrayContext.
 * @returns {AddTrayContextProps} The context value of the add tray context.
 */
export const useAddTrayContext = (): AddTrayContextProps => {
  const context = useContext(AddTrayContext)

  if (!context) {
    throw new Error('useAddTrayContext must be used with AddTrayProvider')
  }

  return context
}
